/**
 * What the confirmation dialog says, per tool.
 *
 * The server sends the paused call's arguments (entity ids) and ADK's own
 * generic hint — "Please approve or reject the tool call merge_artifacts()".
 * That is accurate and useless to a researcher, so the wording lives here,
 * where it can name the actual consequence.
 *
 * A tool with no entry falls back to the hint rather than rendering nothing:
 * a new confirmation tool gets a plain-but-working dialog on day one, and the
 * copy can be written later. Adding an entry is the only step, which is why
 * this is a map and not a switch.
 */

export interface ConfirmationCopy {
  /** The question, as a heading. */
  readonly title: string;
  /** What will happen, including anything irreversible. */
  readonly detail: string;
  /** The affirmative button. Names the action, not "OK". */
  readonly confirmLabel: string;
  /** True when the action destroys data — the button turns destructive. */
  readonly destructive: boolean;
}

/** Count the entity ids in a paused call's arguments, if it has any. */
function idCount(args: Readonly<Record<string, unknown>>, key: string): number {
  const value = args[key];
  return Array.isArray(value) ? value.length : 0;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

type CopyBuilder = (args: Readonly<Record<string, unknown>>) => ConfirmationCopy;

const BUILDERS: Readonly<Record<string, CopyBuilder>> = {
  merge_artifacts: (args) => {
    const total = idCount(args, 'artifactIds');
    const deleted = Math.max(total - 1, 0);
    return {
      title: `Merge ${total} ${plural(total, 'note', 'notes')}?`,
      // Naming the survivor is what makes the deletion count add up. Without
      // it "merge 4, delete 3" reads as a discrepancy.
      // NOT "permanently deleted": step 3 of the merge service soft-deletes the
      // sources to Trash. The restore is a PARTIAL one though, and that is the
      // part worth the words — step 2 re-parents the sources' attachments to
      // the survivor first, so a restored source comes back stripped.
      detail:
        `The oldest note survives and receives all the content. ` +
        `The other ${deleted} ${plural(deleted, 'note moves', 'notes move')} to Trash; ` +
        `their attachments stay on the survivor.`,
      confirmLabel: 'Merge notes',
      destructive: true,
    };
  },
  delete_artifact: (args) => {
    const total = idCount(args, 'artifactIds');
    return {
      title: `Delete ${total} ${plural(total, 'item', 'items')}?`,
      // NOT "this cannot be undone", which is what this said until the tool
      // was exposed to chat and someone read the service: `deleteArtifact`
      // soft-deletes, so Trash restores it. The cascade is the part worth the
      // words instead — it is the consequence a researcher will not predict,
      // and unlike the permanence claim it is true.
      detail:
        'Deleted items move to Trash and can be restored. ' +
        'Deleting a note also deletes its attachments.',
      confirmLabel: plural(total, 'Delete item', 'Delete items'),
      destructive: true,
    };
  },
  fetch_url: (args) => {
    // The full address, query included and never shortened: that is where data
    // the page asked the assistant to send would ride, and a padded path would
    // push it past any cut. fetch_url refuses a URL over 2048 characters.
    const shown = typeof args.url === 'string' ? args.url : '';
    return {
      title: 'Open a link you did not send?',
      detail:
        `The assistant found this link itself and wants to read it: ${shown || 'an unknown address'}. ` +
        'Opening it sends the whole address to that site, so allow it only if you trust it.',
      confirmLabel: 'Open link',
      destructive: false,
    };
  },
  create_visual_reference_set: () => ({
    title: 'Create this visual reference set?',
    detail: 'A new collection will be created from the images in the preview above.',
    confirmLabel: 'Create set',
    destructive: false,
  }),
};

export function confirmationCopy(
  tool: string,
  args: Readonly<Record<string, unknown>>,
  hint: string,
): ConfirmationCopy {
  const builder = BUILDERS[tool];
  if (builder) return builder(args);
  return {
    title: 'Confirm this action?',
    // ADK's hint is the only thing we know about an unmapped tool. Better a
    // slightly technical sentence than an empty dialog.
    detail: hint || 'The assistant is waiting for your approval before continuing.',
    confirmLabel: 'Confirm',
    // Unknown tools are treated as destructive: a confirmation was demanded,
    // so something consequential is behind it.
    destructive: true,
  };
}
